import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Check, Flame, ArrowRight, ShieldCheck, CreditCard, Wallet, Zap, KeyRound, Clock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MONTHLY_PLANS = [
  {
    key: "free", name: "Free", price: 0, period: "forever", tag: "Try it out",
    to: null,
    bullets: [
      "3 press-ready exports / month",
      "Halftone Studio · 3 styles",
      "1 AI tool try / month",
      "Watermark-free downloads",
    ],
    cta: "Start free",
    ctaTo: "/signup",
  },
  {
    key: "pro-monthly", name: "Pro", price: 20, period: "month", tag: "Best for solo shops",
    to: "/purchase?plan=pro-monthly",
    bullets: [
      "Unlimited Halftone exports",
      "All 12 halftone styles",
      "Smart underbase + fabric picker",
      "5 AI tool uses / month",
      "Priority email support",
    ],
    cta: "Get Pro — $20/mo",
  },
  {
    key: "creator-monthly", name: "Creator", price: 45, period: "month", tag: "Most popular", highlight: true,
    to: "/purchase?plan=creator-monthly",
    bullets: [
      "Everything in Pro",
      "AI Vector Creator — unlimited",
      "Watermark Remover — unlimited",
      "Photo Upgrader (Evoto-style) — unlimited",
      "Auto DTF/DTG optimize on every image",
      "Priority artwork help",
    ],
    cta: "Get Creator — $45/mo",
  },
];

const LIFETIME_PLANS = [
  {
    key: "halftone-lifetime", name: "Halftone Lifetime", price: 50, period: "one-time", tag: "Set-it-and-forget-it",
    to: "/purchase?plan=halftone-lifetime",
    bullets: [
      "Unlimited Halftone exports — forever",
      "All 12 halftone styles",
      "Smart underbase + fabric picker",
      "Every future halftone update",
      "10 AI tool uses / month",
    ],
    cta: "Get Halftone Lifetime — $50",
  },
  {
    key: "all-access-lifetime", name: "All-Access Lifetime", price: 100, period: "one-time", tag: "Best value · pays for itself", highlight: true,
    to: "/purchase?plan=all-access-lifetime",
    bullets: [
      "Everything in Halftone Lifetime",
      "AI Vector Creator — unlimited forever",
      "Watermark Remover — unlimited forever",
      "Photo Upgrader — unlimited forever",
      "Auto DTF/DTG optimize on every image",
      "Every future tool we ship — free",
    ],
    cta: "Get All-Access — $100",
  },
];

const FEATURE_ROWS = [
  { label: "Press-ready exports / month", cols: ["3", "Unlimited", "Unlimited", "Unlimited", "Unlimited"] },
  { label: "All 12 halftone styles", cols: ["3 styles", "✓", "✓", "✓", "✓"] },
  { label: "Smart underbase + fabric picker", cols: ["✓", "✓", "✓", "✓", "✓"] },
  { label: "AI Vector Creator", cols: ["1/mo", "5/mo", "Unlimited", "10/mo", "Unlimited"] },
  { label: "Watermark Remover", cols: ["1/mo", "5/mo", "Unlimited", "10/mo", "Unlimited"] },
  { label: "Photo Upgrader (Evoto-style)", cols: ["1/mo", "5/mo", "Unlimited", "10/mo", "Unlimited"] },
  { label: "Auto DTF/DTG optimize", cols: ["—", "—", "✓", "—", "✓"] },
  { label: "Priority support", cols: ["—", "✓", "✓", "—", "✓"] },
  { label: "Future tools included", cols: ["—", "—", "—", "—", "✓"] },
];

const HOW_TO_PAY_STEPS = [
  { icon: CreditCard, title: "Pick your plan", body: "Free, Monthly (Pro or Creator) or Lifetime. No auto-renew traps." },
  { icon: Wallet, title: "Pay with Stripe", body: "Card, Apple Pay, Google Pay, bank — Stripe handles it all. Secure, encrypted, PCI-DSS Level 1." },
  { icon: Zap, title: "Plan activates instantly", body: "The moment Stripe confirms, your Furnace account unlocks. No emails to send, no waiting." },
  { icon: KeyRound, title: "Manage from your inbox", body: "Cancel monthly subs anytime from your Stripe email receipt. Lifetime is yours forever." },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [billing, setBilling] = React.useState("monthly"); // monthly | lifetime

  const plans = billing === "monthly" ? MONTHLY_PLANS : [MONTHLY_PLANS[0], ...LIFETIME_PLANS];

  return (
    <div className="grain">
      <Navbar />
      <section className="relative py-20">
        <div className="ember-glow absolute inset-0" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <div className="text-center">
            <span className="chip mx-auto"><Flame className="h-3 w-3 text-[#4aa4ff]" /> Pricing</span>
            <h1 className="display-font mt-4 text-5xl font-bold tracking-tight sm:text-6xl">Simple. Honest. <span className="ember-text">Yours.</span></h1>
            <p className="mt-4 text-muted-foreground">Free tier for casual shops. Monthly plans start at $20. Lifetime plans start at $50. No auto-renew, no card on file.</p>

            {/* Billing toggle */}
            <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-border bg-card p-1">
              <button onClick={() => setBilling("monthly")}
                className={`rounded-full px-5 py-2 text-sm transition ${billing === "monthly" ? "bg-[#4aa4ff] text-[#05070d] font-semibold" : "text-muted-foreground hover:text-foreground"}`}>
                Monthly
              </button>
              <button onClick={() => setBilling("lifetime")}
                className={`rounded-full px-5 py-2 text-sm transition ${billing === "lifetime" ? "bg-[#4aa4ff] text-[#05070d] font-semibold" : "text-muted-foreground hover:text-foreground"}`}>
                Lifetime <span className="ml-1 rounded-full bg-[#f5c845] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[#1a1305]">Save</span>
              </button>
            </div>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((p) => (
              <div key={p.key} className={`relative rounded-2xl border p-7 ${p.highlight ? "chrome-border shadow-xl shadow-blue-950/40" : "border-border bg-card"}`}>
                {p.highlight && <div className="absolute -top-3 left-6 rounded-full bg-[#4aa4ff] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#05070d]">{p.tag}</div>}
                {!p.highlight && <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{p.tag}</div>}
                <h3 className="display-font mt-2 text-2xl font-bold">{p.name}</h3>
                <div className="mt-4 flex items-end gap-1">
                  <span className="display-font text-5xl font-bold">${p.price}</span>
                  <span className="mb-1 text-sm text-muted-foreground">/ {p.period}</span>
                </div>
                <ul className="mt-6 space-y-2 text-sm">
                  {p.bullets.map((b) => (<li key={b} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 tick" /><span className="text-foreground/90">{b}</span></li>))}
                </ul>
                {p.ctaTo || p.to ? (
                  <Button className={`mt-7 h-11 w-full font-semibold ${p.highlight ? "btn-ember" : ""}`} variant={p.highlight ? "default" : "outline"} onClick={() => navigate(p.ctaTo || p.to)}>{p.cta} <ArrowRight className="ml-2 h-4 w-4" /></Button>
                ) : null}
                {p.key !== "free" && <p className="mt-2 text-center text-[11px] text-muted-foreground">Secure Stripe checkout · card / Apple Pay / Google Pay</p>}
              </div>
            ))}
          </div>

          {/* How to pay */}
          <div className="mt-16 rounded-3xl border border-border bg-gradient-to-br from-[#08101c] to-card p-10">
            <div className="text-center">
              <span className="chip mx-auto"><CreditCard className="h-3 w-3 text-[#4aa4ff]" /> How you pay</span>
              <h2 className="display-font mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Secure Stripe checkout — instant activation.</h2>
              <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">Card, Apple Pay, Google Pay and bank supported. Your plan unlocks the moment Stripe confirms your payment. Cancel monthly subscriptions anytime from your Stripe receipt.</p>
            </div>
            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {HOW_TO_PAY_STEPS.map((s, i) => (
                <div key={s.title} className="relative rounded-2xl border border-border bg-card p-5">
                  <span className="mono absolute right-4 top-4 text-[10px] uppercase text-muted-foreground">0{i + 1}</span>
                  <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-[#4aa4ff]"><s.icon className="h-5 w-5" /></div>
                  <h3 className="display-font text-base font-bold">{s.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Feature comparison */}
          <div className="mt-16 overflow-hidden rounded-2xl border border-border">
            <div className="grid grid-cols-6 border-b border-border bg-secondary/40 px-6 py-4 text-sm font-semibold">
              <div className="col-span-1">Features</div>
              <div>Free</div>
              <div>Pro $20</div>
              <div>Creator $45</div>
              <div>Halftone $50</div>
              <div>All-Access $100</div>
            </div>
            {FEATURE_ROWS.map((r, i) => (
              <div key={i} className={`grid grid-cols-6 px-6 py-3 text-sm ${i % 2 ? "bg-background" : "bg-card/50"}`}>
                <div className="col-span-1 text-muted-foreground">{r.label}</div>
                {r.cols.map((c, j) => <div key={j} className={c === "✓" ? "text-[#4aa4ff] font-semibold" : ""}>{c}</div>)}
              </div>
            ))}
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-8">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-6 w-6 text-[#4aa4ff]" />
                <h3 className="display-font text-xl font-bold">15-day money-back guarantee</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">Not happy? Email us within 15 days and we refund it. No forms, no questions.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-8">
              <div className="flex items-center gap-3">
                <KeyRound className="h-6 w-6 text-[#f5c845]" />
                <h3 className="display-font text-xl font-bold">Already got a code?</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">Redeem it in your account for instant Pro or Creator access.</p>
              <Link to="/redeem"><Button variant="outline" className="mt-4 gap-2">Redeem code <ArrowRight className="h-3.5 w-3.5" /></Button></Link>
            </div>
          </div>

          {!user && (
            <div className="mt-16 rounded-2xl chrome-border p-10 text-center">
              <h3 className="display-font text-3xl font-bold">Start free — no credit card.</h3>
              <p className="mt-2 text-muted-foreground">3 press-ready exports this month. All 12 halftone styles unlocked.</p>
              <Button className="btn-ember mt-6 h-11 px-6 font-semibold" onClick={() => navigate("/signup")}>Create your account</Button>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
